import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminQueueItem } from "../../shared/api";
import { metaFor } from "../../shared/seo";
import { api } from "../lib/api";
import { useMeta } from "../lib/seo";
import { origin } from "../lib/origin";
import { useHotkeys } from "../lib/useHotkeys";
import { Spinner, Empty } from "../components/States";
import { VerificationBadge } from "../components/Badge";
import { Stars } from "../components/Stars";
import { TimeAgo } from "../lib/format";

// Triage console (SPEC §5). Built for one operator with minutes: everything
// needed to decide is on screen, and the keyboard does the work.

export function Admin() {
  const [items, setItems] = useState<AdminQueueItem[] | null>(null);
  const [denied, setDenied] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [reason, setReason] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef<HTMLInputElement>(null);

  useMeta({ ...metaFor({ route: "notfound", base: origin() }), title: "Moderation · CryptoChit" });

  const load = useCallback(async () => {
    try {
      const page = await api.adminQueue();
      setItems(page.items);
      setDenied(false);
    } catch (err) {
      // The gate 404s for non-staff by design, so this is the expected path for
      // anyone who should not see the console at all.
      if ((err as { status?: number }).status === 404) setDenied(true);
      else setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const current = items && items.length > 0 ? items[Math.min(cursor, items.length - 1)] : null;

  /** Drop the item locally so the operator never waits on a round trip. */
  const act = useCallback(
    async (fn: () => Promise<unknown>, id: string) => {
      if (busy) return;
      setBusy(true);
      setItems((prev) => (prev ? prev.filter((i) => i.id !== id) : prev));
      setReason("");
      try {
        await fn();
      } finally {
        setBusy(false);
        void load();
      }
    },
    [busy, load],
  );

  const publish = useCallback(() => {
    if (!current?.review) return;
    void act(() => api.adminPublish(current.targetId), current.id);
  }, [current, act]);

  const remove = useCallback(() => {
    if (!current?.review) return;
    void act(() => api.adminRemove(current.targetId, reason), current.id);
  }, [current, reason, act]);

  useHotkeys({
    j: () => setCursor((c) => (items ? Math.min(c + 1, items.length - 1) : c)),
    k: () => setCursor((c) => Math.max(c - 1, 0)),
    p: publish,
    r: () => reasonRef.current?.focus(),
    "?": () => setShowHelp((s) => !s),
  });

  if (denied) {
    return (
      <div className="empty-state pad-empty-lg">
        <h1>404</h1>
        <p>That page doesn't exist.</p>
        <Link to="/" className="btn btn-primary">Go home</Link>
      </div>
    );
  }
  if (!items) return <Spinner />;

  return (
    <div>
      <div className="row between items-baseline">
        <h1>Moderation</h1>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowHelp((s) => !s)}>
          Shortcuts (?)
        </button>
      </div>

      {showHelp && (
        <div className="card u-bf05e0e">
          <span className="mono">j</span>/<span className="mono">k</span> move ·{" "}
          <span className="mono">p</span> publish · <span className="mono">r</span> focus reason ·{" "}
          <span className="mono">?</span> toggle this
        </div>
      )}

      {items.length === 0 ? (
        <Empty title="Queue clear" hint="Nothing is waiting on a decision." />
      ) : (
        <div className="grid u-f3d7ce7">
          {items.map((item, i) => {
            const selected = i === Math.min(cursor, items.length - 1);
            return (
              <article
                key={item.id}
                className={`card p-14 clickable${selected ? " selected" : ""}`}
                onClick={() => setCursor(i)}
              >
                <div className="row u-4173603">
                  <span className="row gap-8">
                    {item.review ? (
                      <Link to={`/${item.review.entityType}/${item.review.entitySlug}`}>
                        <strong>{item.review.entityName}</strong>
                      </Link>
                    ) : (
                      <strong>{item.targetType}</strong>
                    )}
                    {item.review && <Stars value={item.review.rating} />}
                    {item.review && <VerificationBadge level={item.review.verificationLevel} />}
                  </span>
                  <span className="muted u-c956592">
                    <TimeAgo iso={item.createdAt} />
                  </span>
                </div>

                <div className="row u-663cc35">
                  {item.reasons.map((r) => (
                    <span key={r} className="badge badge-warn">{r.replace(/_/g, " ")}</span>
                  ))}
                </div>

                {item.review?.title && <h3 className="u-557d84c">{item.review.title}</h3>}
                {item.review?.body && <p className="u-557d84c">{item.review.body}</p>}

                {item.author && (
                  <p className="muted u-a35a800">
                    @{item.author.username} · reputation {Math.round(item.author.reputation)} ·
                    account {Math.round(item.author.accountAgeHours)}h old
                    {item.author.priorRemovals > 0
                      ? ` · ${item.author.priorRemovals} prior removals`
                      : ""}
                    {item.reasons.includes("entity_burst") && item.review
                      ? ` · ${item.review.entityReviewsLastHour} reviews on this entity in the last hour`
                      : ""}
                  </p>
                )}

                {item.submission && (
                  <div className="mt-10">
                    <p className="muted u-fb32288">
                      New {item.submission.type} submitted
                      {item.submission.submittedBy ? <> by @{item.submission.submittedBy}</> : null}.
                    </p>
                    <div className="card u-4e586e9 grid gap-6">
                      <div>
                        <strong>{item.submission.name}</strong>{" "}
                        {item.submission.symbol && (
                          <span className="muted mono">{item.submission.symbol}</span>
                        )}
                      </div>
                      {item.submission.shortDesc && <div>{item.submission.shortDesc}</div>}
                      {item.submission.website && (
                        <div>
                          <a href={item.submission.website} target="_blank" rel="noopener noreferrer nofollow">
                            {item.submission.website}
                          </a>
                        </div>
                      )}
                      {item.submission.contractAddress && (
                        <div className="mono u-fb32288">
                          {item.submission.chain}: {item.submission.contractAddress}{" "}
                          <a
                            href={`https://etherscan.io/token/${item.submission.contractAddress}`}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                          >
                            check on explorer
                          </a>
                        </div>
                      )}
                    </div>
                    {selected && (
                      <div className="row gap-8 mt-10">
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={busy}
                          onClick={() =>
                            void act(() => api.adminApproveEntity(item.submission!.entityId), item.id)
                          }
                        >
                          Approve &amp; publish
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() =>
                            void act(
                              () => api.adminRejectEntity(item.submission!.entityId, reason || "not accepted"),
                              item.id,
                            )
                          }
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {item.cluster && (
                  <div className="mt-10">
                    <p className="muted u-fb32288">
                      Coordinated ring on <strong>{item.cluster.entityName}</strong> —{" "}
                      {item.cluster.memberCount} reviews.{" "}
                      {item.cluster.ratingSwing != null && (
                        <>
                          Removing them would move the rating by{" "}
                          <strong className="rating-num">
                            {item.cluster.ratingSwing > 0 ? "+" : ""}
                            {item.cluster.ratingSwing.toFixed(2)}
                          </strong>
                          .
                        </>
                      )}
                    </p>
                    <div className="grid gap-6">
                      {item.cluster.members.slice(0, 8).map((m) => (
                        <div key={m.id} className="card u-4e586e9">
                          <span className="mono">{m.rating}★</span> @{m.username} — {m.body ?? "(no body)"}
                        </div>
                      ))}
                    </div>
                    {selected && (
                      <div className="row gap-8 mt-10">
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() =>
                            void act(() => api.adminResolveCluster(item.cluster!.id, "confirmed"), item.id)
                          }
                        >
                          Confirm ring — remove all {item.cluster.memberCount}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() =>
                            void act(() => api.adminResolveCluster(item.cluster!.id, "dismissed"), item.id)
                          }
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {selected && item.review && (
                  <div className="row gap-8 mt-12">
                    <button className="btn btn-primary btn-sm" onClick={publish} disabled={busy}>
                      Publish
                    </button>
                    <input
                      ref={reasonRef}
                      className="input u-eb62184"
                      placeholder="Removal reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <button className="btn btn-ghost btn-sm" onClick={remove} disabled={busy}>
                      Remove
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
